/**
 * Radius — Network > RADIUS Realms (the managed-RADIUS / Passpoint keystone).
 *
 * Org-scoped realms + their credential store. Pick a realm to manage its credentials:
 * "Provision guest" issues a credential AND hands back an installable Passpoint profile
 * (.mobileconfig for iOS/macOS, PPS-MO for Android) with the one-time password shown once and
 * embedded in the download. Revoke kills a credential at checkout. Reuses the Meridian
 * list+dialog idiom and design tokens; the status ramp is used only for credential STATE.
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  useRadiusRealms,
  useCreateRadiusRealm,
  useRadiusCredentials,
  useProvisionGuest,
  useRevokeRadiusCredential,
} from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { RoleGate } from "@/components/auth/RoleGate";
import { fmtAbsolute } from "@/lib/format";
import type { RadiusRealm, ProvisionResult } from "@/types/domain";

// EAP methods offered in the UI, friendliest (username/password) first.
const METHODS: { value: string; label: string }[] = [
  { value: "eap-ttls-pap", label: "EAP-TTLS / PAP (username & password)" },
  { value: "eap-peap-mschapv2", label: "EAP-PEAP / MSCHAPv2 (username & password)" },
  { value: "eap-tls", label: "EAP-TLS (client certificate)" },
];
const METHOD_SHORT: Record<string, string> = {
  "eap-ttls-pap": "TTLS/PAP",
  "eap-peap-mschapv2": "PEAP/MSCHAPv2",
  "eap-tls": "TLS",
};
const STATE_RAMP: Record<string, "healthy" | "warn" | "critical"> = {
  active: "healthy",
  expired: "warn",
  revoked: "critical",
};

/** Turn a returned profile string into a browser download (real app, not an artifact). */
function downloadProfile(res: ProvisionResult) {
  const blob = new Blob([res.profile], { type: res.content_type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = res.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Radius() {
  const { data, isLoading, error } = useRadiusRealms();
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const realms = data?.realms ?? [];
  const selected = realms.find((r) => r.id === selectedId) ?? null;

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <PageHeader
          title="RADIUS Realms"
          subtitle="Managed AAA for Passpoint & enterprise Wi-Fi — issue and revoke per-guest credentials"
        />
        <RoleGate min="org_admin" mode="hide">
          <Button variant="primary" onClick={() => setCreating(true)}>
            Add realm
          </Button>
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading realms…</div>}

      {data && realms.length === 0 && (
        <EmptyState
          title="No realms yet"
          hint="A realm is your tenant's NAI domain (e.g. guest.hotel.example). Create one, then issue guest or staff credentials against it — and provision devices with a scan-once profile."
        />
      )}

      {realms.length > 0 && (
        <ul className="flex flex-col gap-2">
          {realms.map((r) => {
            const active = r.id === selectedId;
            return (
              <li
                key={r.id}
                className={`md-card cursor-pointer p-3 transition-colors ${
                  active ? "border-brand" : "hover:border-line-strong"
                }`}
                onClick={() => setSelectedId(active ? null : r.id)}
              >
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-ink">{r.label || r.realm}</span>
                  {!r.enabled && (
                    <span className="rounded border border-line px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-ink-subtle">
                      disabled
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 font-mono text-xs text-ink-muted">
                  <span>{r.realm}</span>
                  <span className="not-italic">default {METHOD_SHORT[r.default_method] ?? r.default_method}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {selected && <RealmCredentials realm={selected} />}
      {creating && <RealmDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

/** The selected realm's credential store + the provision action. */
function RealmCredentials({ realm }: { realm: RadiusRealm }) {
  const { data, isLoading, error } = useRadiusCredentials(realm.id);
  const revoke = useRevokeRadiusCredential(realm.id);
  const [provisioning, setProvisioning] = useState(false);
  const creds = data?.credentials ?? [];

  return (
    <section className="mt-6">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-subtle">
          Credentials · <span className="font-mono normal-case text-ink-muted">{realm.realm}</span>
        </h2>
        <RoleGate min="org_admin" mode="hide">
          <Button variant="primary" onClick={() => setProvisioning(true)}>
            Provision guest
          </Button>
        </RoleGate>
      </div>

      {error && <ErrorBanner error={error} />}
      {isLoading && <div className="text-sm text-ink-muted">Loading credentials…</div>}

      {data && creds.length === 0 && (
        <EmptyState
          title="No active credentials"
          hint="Provision a guest to issue the first credential and hand off an install profile."
        />
      )}

      {creds.length > 0 && (
        <ul className="flex flex-col gap-2">
          {creds.map((c) => (
            <li key={c.id} className="md-card flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-mono text-sm text-ink">{c.username}</span>
                  <span className="rounded border border-line px-1.5 py-0.5 text-[0.625rem] uppercase tracking-wide text-ink-subtle">
                    {METHOD_SHORT[c.method] ?? c.method}
                  </span>
                  <StatusPill status={STATE_RAMP[c.state] ?? "info"}>{c.state}</StatusPill>
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ink-muted">
                  {c.issued_for && <span>{c.issued_for}</span>}
                  {c.vlan_id != null && <span className="tabular-nums">VLAN {c.vlan_id}</span>}
                  {c.expires_at && <span>expires {fmtAbsolute(c.expires_at)}</span>}
                  <span>{c.last_auth_at ? `last auth ${fmtAbsolute(c.last_auth_at)}` : "never authed"}</span>
                </div>
              </div>
              {c.state === "active" && (
                <RoleGate min="org_admin" mode="hide">
                  <Button variant="secondary" disabled={revoke.isPending}
                    onClick={() => revoke.mutate(c.id)}>
                    Revoke
                  </Button>
                </RoleGate>
              )}
            </li>
          ))}
        </ul>
      )}

      {provisioning && <ProvisionDialog realm={realm} onClose={() => setProvisioning(false)} />}
    </section>
  );
}

/** Create/get-or-create a realm. */
function RealmDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateRadiusRealm();
  const [realm, setRealm] = useState("");
  const [label, setLabel] = useState("");
  const [method, setMethod] = useState("eap-ttls-pap");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!realm.trim()) return;
    create.mutate(
      { realm: realm.trim(), label: label.trim() || undefined, default_method: method },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Add RADIUS realm</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            A realm is your tenant&rsquo;s NAI domain. It&rsquo;s what Passpoint advertises and what a
            provisioned device&rsquo;s identity resolves to (username@realm).
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Realm (NAI domain)</span>
              <input value={realm} onChange={(e) => setRealm(e.target.value)} placeholder="guest.hotel.example"
                className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand" />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Label (optional)</span>
              <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Guest Wi-Fi"
                className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand" />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-ink-muted">Default EAP method</span>
              <select value={method} onChange={(e) => setMethod(e.target.value)}
                className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand">
                {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </label>
            {create.isError && <span className="text-xs text-critical">Could not create the realm.</span>}
            <div className="md-dialog-footer flex items-center justify-end gap-2">
              <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
              <Button variant="primary" type="submit" disabled={create.isPending}>
                {create.isPending ? "Saving…" : "Create"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Issue a credential + hand off an install profile. Shows the one-time password once. */
function ProvisionDialog({ realm, onClose }: { realm: RadiusRealm; onClose: () => void }) {
  const provision = useProvisionGuest(realm.id);
  const [platform, setPlatform] = useState<"ios" | "android">("ios");
  const [method, setMethod] = useState(realm.default_method || "eap-ttls-pap");
  const [issuedFor, setIssuedFor] = useState("");
  const [vlan, setVlan] = useState("");
  const [expires, setExpires] = useState("");
  const result = provision.data;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    provision.mutate(
      {
        platform,
        method,
        issued_for: issuedFor.trim() || undefined,
        vlan_id: vlan ? Number(vlan) : undefined,
        // datetime-local (local time) -> ISO-8601 for the server.
        expires_at: expires ? new Date(expires).toISOString() : undefined,
      },
      { onSuccess: (res) => downloadProfile(res) },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Provision guest</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Issues a credential in <span className="font-mono">{realm.realm}</span> and downloads an
            install profile. The password is shown once.
          </Dialog.Description>

          {result ? (
            <div className="mt-4 flex flex-col gap-3">
              <div className="rounded border border-healthy/40 bg-surface-2 p-3">
                <div className="text-xs uppercase tracking-wide text-ink-subtle">Username</div>
                <div className="font-mono text-sm text-ink">{result.credential.username}</div>
                {result.credential.password && (
                  <>
                    <div className="mt-2 text-xs uppercase tracking-wide text-ink-subtle">
                      One-time password (won&rsquo;t be shown again)
                    </div>
                    <div className="select-all break-all font-mono text-sm text-ink">
                      {result.credential.password}
                    </div>
                  </>
                )}
              </div>
              <p className="text-xs text-ink-muted">
                The {result.platform === "android" ? "Android PPS-MO" : "Apple .mobileconfig"} profile
                downloaded automatically — install it on the guest device to auto-connect.
              </p>
              <div className="flex items-center justify-end gap-2">
                <Button variant="secondary" type="button" onClick={() => downloadProfile(result)}>
                  Download again
                </Button>
                <Button variant="primary" type="button" onClick={onClose}>Done</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Device platform</span>
                <select value={platform} onChange={(e) => setPlatform(e.target.value as "ios" | "android")}
                  className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand">
                  <option value="ios">iOS / macOS (.mobileconfig)</option>
                  <option value="android">Android (PPS-MO)</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">EAP method</span>
                <select value={method} onChange={(e) => setMethod(e.target.value)}
                  className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand">
                  {/* eap-tls is now offered: a trusted CA (Vault PKI) is live, so issued client
                      certs chain to a real CA. Previously filtered out while only the self-signing
                      dev backend existed. */}
                  {METHODS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-ink-muted">Issued for (room / guest / note, optional)</span>
                <input value={issuedFor} onChange={(e) => setIssuedFor(e.target.value)} placeholder="Room 214"
                  className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">VLAN (optional)</span>
                  <input inputMode="numeric" value={vlan}
                    onChange={(e) => setVlan(e.target.value.replace(/[^0-9]/g, ""))} placeholder="30"
                    className="rounded border border-line bg-surface px-3 py-2 font-mono text-ink outline-none focus:border-brand" />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-ink-muted">Expires (optional)</span>
                  <input type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)}
                    className="rounded border border-line bg-surface px-3 py-2 text-ink outline-none focus:border-brand" />
                </label>
              </div>
              {provision.isError && <span className="text-xs text-critical">Could not provision.</span>}
              <div className="mt-1 flex items-center justify-end gap-2">
                <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={provision.isPending}>
                  {provision.isPending ? "Provisioning…" : "Provision & download"}
                </Button>
              </div>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
